import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Settings,
  Save,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Layers,
  BookOpen,
  Award,
  Search,
  RotateCcw,
  Sliders,
  Plus
} from 'lucide-react';

export const AssessmentConfigPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();

  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form State
  const [form, setForm] = useState({
    academic_session_id: '3',
    month: 'September',
    class_id: '1',
    stream: 'Science',
    subject_id: 'all',
    assessment_category: 'MONTHLY',
    maximum_marks: '25',
    passing_marks: '8.25',
    allow_decimals: true,
    apply_to_all_subjects: true
  });

  // Filter state for table
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');

  const monthsList = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  const subjectsMap: Record<string, { id: number; name: string; code: string }[]> = {
    class10: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 3, name: 'Science', code: 'SCI-103' },
      { id: 7, name: 'Hindi', code: 'HIN-107' },
      { id: 8, name: 'Social Science', code: 'SOC-108' },
      { id: 9, name: 'Sanskrit', code: 'SKT-109' },
      { id: 10, name: 'Punjabi', code: 'PJB-110' }
    ],
    class12_Science: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 4, name: 'Physics', code: 'PHY-104' },
      { id: 5, name: 'Chemistry', code: 'CHM-105' },
      { id: 6, name: 'Biology', code: 'BIO-106' },
      { id: 11, name: 'Computer Science', code: 'CSC-111' },
      { id: 12, name: 'Physical Education', code: 'PED-112' }
    ],
    class12_Arts: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 7, name: 'Hindi', code: 'HIN-107' },
      { id: 16, name: 'History', code: 'HIS-116' },
      { id: 17, name: 'Political Science', code: 'POL-117' },
      { id: 18, name: 'Geography', code: 'GEO-118' },
      { id: 15, name: 'Economics', code: 'ECO-115' },
      { id: 19, name: 'Sociology', code: 'SOC-119' },
      { id: 20, name: 'Psychology', code: 'PSY-120' },
      { id: 23, name: 'Home Science', code: 'HSC-123' }
    ],
    class12_Commerce: [
      { id: 1, name: 'English', code: 'ENG-101' },
      { id: 13, name: 'Accountancy', code: 'ACC-113' },
      { id: 14, name: 'Business Studies', code: 'BST-114' },
      { id: 15, name: 'Economics', code: 'ECO-115' },
      { id: 2, name: 'Mathematics', code: 'MTH-102' },
      { id: 22, name: 'Entrepreneurship', code: 'ENT-122' },
      { id: 21, name: 'Informatics Practices', code: 'IP-121' }
    ]
  };

  const currentSubjectOptions = form.class_id === '1'
    ? subjectsMap.class10
    : form.stream === 'Science'
    ? subjectsMap.class12_Science
    : form.stream === 'Commerce'
    ? subjectsMap.class12_Commerce
    : subjectsMap.class12_Arts;

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (filterMonth !== 'all') query.set('month', filterMonth);
      if (filterClass !== 'all') query.set('class_id', filterClass);

      const res = await fetchWithAuth(`/api/admin/assessment-configs?${query.toString()}`);
      const json = await res.json();
      if (json.success) {
        setConfigs(json.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
  }, [filterMonth, filterClass]);

  const handleMaxMarksChange = (val: string) => {
    const num = parseFloat(val);
    const pass = !isNaN(num) ? (Math.round(num * 0.33 * 10) / 10).toString() : '';
    setForm(prev => ({
      ...prev,
      maximum_marks: val,
      passing_marks: pass
    }));
  };

  const handlePresetSelect = (presetMarks: number) => {
    handleMaxMarksChange(String(presetMarks));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    const maxVal = parseFloat(form.maximum_marks);
    if (isNaN(maxVal) || maxVal <= 0) {
      setMessage({ type: 'error', text: 'Maximum marks must be a valid positive number.' });
      setSaving(false);
      return;
    }

    try {
      const payload = {
        academic_session_id: parseInt(form.academic_session_id, 10),
        month: form.month,
        class_id: parseInt(form.class_id, 10),
        stream: form.class_id === '2' ? form.stream : null,
        subject_id: form.subject_id === 'all' ? null : parseInt(form.subject_id, 10),
        assessment_category: form.assessment_category,
        maximum_marks: maxVal,
        passing_marks: form.passing_marks ? parseFloat(form.passing_marks) : null,
        allow_decimals: form.allow_decimals ? 1 : 0,
        apply_to_all_subjects: form.subject_id === 'all' || form.apply_to_all_subjects
      };

      const res = await fetchWithAuth('/api/admin/assessment-configs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (json.success) {
        setMessage({ type: 'success', text: json.message || 'Assessment configuration saved successfully.' });
        loadConfigs();
      } else {
        setMessage({ type: 'error', text: json.message || 'Failed to save configuration.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Network error saving configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this assessment configuration?')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/assessment-configs/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setMessage({ type: 'success', text: 'Configuration removed successfully.' });
        loadConfigs();
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Failed to delete configuration.' });
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="text-xs font-bold text-amber-600 uppercase tracking-wider">
            DEO / District Administration
          </div>
          <h1 className="text-lg md:text-xl font-bold text-[#0F2C59]">
            Assessment Maximum Marks & Subject Configuration
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            Configure custom Maximum Marks per Academic Session, Month, Class, Stream, and Subject
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <span className="bg-blue-100 text-blue-900 border border-blue-200 text-xs font-bold px-3 py-1.5 rounded">
            Academic Session 2026–27
          </span>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div className={`p-3 rounded-lg border text-xs flex items-center justify-between ${
          message.type === 'success'
            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
            : 'bg-rose-50 border-rose-300 text-rose-900'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-500 hover:text-slate-800">
            ×
          </button>
        </div>
      )}

      {/* 1. CONFIGURATION FORM */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
        <div className="flex items-center space-x-2 pb-3 border-b border-slate-200 mb-4">
          <Sliders className="w-4 h-4 text-[#0F2C59]" />
          <h2 className="text-sm font-bold text-[#0F2C59]">Configure Assessment Maximum Marks</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {/* Month */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 flex items-center space-x-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span>Assessment Month</span>
              </label>
              <select
                value={form.month}
                onChange={(e) => setForm({ ...form, month: e.target.value })}
                className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-slate-900 bg-slate-50 focus:ring-1 focus:ring-[#0F2C59]"
              >
                {monthsList.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            {/* Class */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 flex items-center space-x-1">
                <Layers className="w-3.5 h-3.5 text-slate-400" />
                <span>Class</span>
              </label>
              <select
                value={form.class_id}
                onChange={(e) => setForm({ ...form, class_id: e.target.value, subject_id: 'all' })}
                className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-slate-900 bg-slate-50 focus:ring-1 focus:ring-[#0F2C59]"
              >
                <option value="1">Class 10 (Secondary)</option>
                <option value="2">Class 12 (Senior Secondary)</option>
              </select>
            </div>

            {/* Stream */}
            <div>
              <label className="block font-bold text-slate-700 mb-1 flex items-center space-x-1">
                <BookOpen className="w-3.5 h-3.5 text-slate-400" />
                <span>Stream</span>
              </label>
              <select
                disabled={form.class_id === '1'}
                value={form.class_id === '1' ? 'N/A' : form.stream}
                onChange={(e) => setForm({ ...form, stream: e.target.value, subject_id: 'all' })}
                className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-slate-900 bg-slate-50 focus:ring-1 focus:ring-[#0F2C59] disabled:opacity-40"
              >
                {form.class_id === '1' ? (
                  <option value="N/A">Not Applicable (Class 10)</option>
                ) : (
                  <>
                    <option value="Arts">Arts</option>
                    <option value="Science">Science</option>
                    <option value="Commerce">Commerce</option>
                  </>
                )}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block font-bold text-slate-700 mb-1">Subject Scope</label>
              <select
                value={form.subject_id}
                onChange={(e) => setForm({ ...form, subject_id: e.target.value })}
                className="w-full border border-slate-300 rounded p-2 text-xs font-bold text-slate-900 bg-slate-50 focus:ring-1 focus:ring-[#0F2C59]"
              >
                <option value="all">⚡ All Subjects (Uniform Max Marks)</option>
                {currentSubjectOptions.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Max Marks & Presets */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div>
              <label className="block font-bold text-[#0F2C59] mb-1 text-xs">
                Configured Maximum Marks (e.g. 20, 25, 30, 50, 100)
              </label>
              <input
                type="number"
                min="1"
                max="200"
                step="0.5"
                required
                value={form.maximum_marks}
                onChange={(e) => handleMaxMarksChange(e.target.value)}
                placeholder="25"
                className="w-full border border-slate-300 rounded p-2 text-sm font-extrabold text-[#0F2C59] bg-white focus:ring-1 focus:ring-[#0F2C59]"
              />
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1 text-xs">
                Minimum Passing Marks (33% Benchmark)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.passing_marks}
                onChange={(e) => setForm({ ...form, passing_marks: e.target.value })}
                placeholder="8.25"
                className="w-full border border-slate-300 rounded p-2 text-sm font-bold text-slate-800 bg-white focus:ring-1 focus:ring-[#0F2C59]"
              />
            </div>

            {/* Quick Presets */}
            <div>
              <label className="block font-bold text-slate-600 mb-1 text-xs">Quick Presets</label>
              <div className="flex flex-wrap gap-1.5">
                {[20, 25, 30, 40, 50, 80, 100].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePresetSelect(p)}
                    className={`px-2.5 py-1 rounded text-xs font-bold border transition ${
                      parseFloat(form.maximum_marks) === p
                        ? 'bg-[#0F2C59] text-white border-[#0F2C59]'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    {p}M
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <div className="flex items-center space-x-2 text-xs text-slate-600">
              <input
                type="checkbox"
                id="apply_all"
                checked={form.apply_to_all_subjects}
                onChange={(e) => setForm({ ...form, apply_to_all_subjects: e.target.checked })}
                className="rounded border-slate-300 text-[#0F2C59] focus:ring-[#0F2C59]"
              />
              <label htmlFor="apply_all" className="cursor-pointer font-semibold">
                Apply this maximum marks setting to all schools and assessments for this selection
              </label>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center space-x-1.5 bg-[#0F2C59] hover:bg-[#1E40AF] text-white px-5 py-2 rounded font-bold text-xs shadow-sm transition disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5 text-amber-400" />
              <span>{saving ? 'Saving Configuration...' : 'Save & Enforce Max Marks'}</span>
            </button>
          </div>
        </form>
      </div>

      {/* 2. ACTIVE CONFIGURATIONS TABLE */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <h3 className="text-sm font-bold text-[#0F2C59]">
              Active Assessment Configurations ({configs.length} rules)
            </h3>
            <div className="text-xs text-slate-500">
              Configured maximum marks automatically override defaults in the School Marks Entry portal.
            </div>
          </div>

          <div className="flex items-center space-x-2 text-xs">
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="bg-white border border-slate-300 py-1 px-2 rounded font-semibold text-slate-800"
            >
              <option value="all">All Months</option>
              {monthsList.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="bg-white border border-slate-300 py-1 px-2 rounded font-semibold text-slate-800"
            >
              <option value="all">All Classes</option>
              <option value="1">Class 10</option>
              <option value="2">Class 12</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-[#0F2C59] text-white uppercase font-bold text-[11px] border-b border-slate-200">
              <tr>
                <th className="py-2.5 px-3">Month</th>
                <th className="py-2.5 px-3">Class</th>
                <th className="py-2.5 px-3">Stream</th>
                <th className="py-2.5 px-3.5">Subject</th>
                <th className="py-2.5 px-3">Category</th>
                <th className="py-2.5 px-3 text-center">Max Marks</th>
                <th className="py-2.5 px-3 text-center">Pass Marks</th>
                <th className="py-2.5 px-3 text-center">Decimals?</th>
                <th className="py-2.5 px-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-500">
                    Loading configurations...
                  </td>
                </tr>
              ) : configs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-slate-500">
                    No custom assessment configurations defined yet. Historical assessments use their recorded maximum marks.
                  </td>
                </tr>
              ) : (
                configs.map(cfg => (
                  <tr key={cfg.id} className="hover:bg-slate-50">
                    <td className="py-2 px-3 font-bold text-slate-900">{cfg.month}</td>
                    <td className="py-2 px-3 font-semibold text-slate-700">{cfg.class_name}</td>
                    <td className="py-2 px-3 text-slate-600">{cfg.stream || 'All'}</td>
                    <td className="py-2 px-3.5 font-semibold text-[#0F2C59]">
                      {cfg.subject_name ? `${cfg.subject_name} (${cfg.subject_code})` : 'All Subjects (Class-wide)'}
                    </td>
                    <td className="py-2 px-3 text-slate-600">{cfg.assessment_category || 'MONTHLY'}</td>
                    <td className="py-2 px-3 text-center">
                      <span className="bg-blue-100 text-blue-900 font-extrabold px-2.5 py-0.5 rounded border border-blue-300 text-xs">
                        {cfg.maximum_marks} Marks
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center font-bold text-slate-700">
                      {cfg.passing_marks || (cfg.maximum_marks * 0.33).toFixed(1)}
                    </td>
                    <td className="py-2 px-3 text-center text-emerald-700 font-bold">
                      {cfg.allow_decimals ? 'Allowed' : 'Integers Only'}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => handleDelete(cfg.id)}
                        className="p-1 rounded text-rose-600 hover:bg-rose-50 hover:text-rose-800 transition"
                        title="Delete Rule"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
